import { useEffect, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Modal } from 'antd';
import { useAppNavigate, usePermission } from '@/hooks';
import SystemHeader from '@/widgets/layout/SystemHeader';
import AppDocContentDisplay from '@/widgets/application/AppDocContentDisplay';
import { applicationDocsApi } from '@/services/applicationDocs/api';
import { isHandledAuthError } from '@/services/client';
import { useToast, StatusBadge } from '@/widgets/common';
import { BackButton } from '@/widgets/demo/ListPrimitives';
import { formatApplicationDocDisplayId, japaneseNameDisplay } from '@/utils/applicationDoc';
import { CASE_STATUSES, caseStatusKeyByLabel, caseStatusPermissions } from '@/constants/enums';

export interface AppDocDataLike {
  id?: string | number;
  applicationNo?: string;
  identId?: string;
  applicantName?: string;
  name?: string;
  applicantPhone?: string;
  applicantEmail?: string;
  currentAddress?: string;
  applicationDate?: string | null;
  caseStatus?: string;
  status?: string;
  householdNo?: string;
  memo?: string;
  protectionResidenceSame?: boolean | string;
  members?: Record<string, string>[] | unknown[] | null;
  pastAddresses?: Record<string, string>[] | unknown[] | null;
  bank?: Record<string, string>;
  otherHouseholdMembers?: Record<string, string>[] | unknown[] | null;
  additionRows?: Record<string, string>[] | unknown[] | null;
  attachments?: Record<string, unknown> | null;
  attachmentStorageAvailable?: boolean;
  attachmentStorageMessage?: string;
  createdAt?: string;
  updatedAt?: string;
}

interface ApplicationDocDetailProps {
  data?: AppDocDataLike;
  setData?: (data: AppDocDataLike) => void;
  showNotification?: (msg: string) => void;
}

function parsePositiveInt(value: string | undefined): number | null {
  if (!value) return null;
  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
}

const applicantNameOf = (doc: AppDocDataLike | null | undefined) => {
  const name = japaneseNameDisplay(doc?.applicantName ?? doc?.name);
  return name || '—';
};
const DEFAULT_ATTACHMENT_STORAGE_MESSAGE = 'S3に接続されていないため、添付画像は表示できません。';

const hasApplicationDocData = (value: AppDocDataLike | null | undefined) => {
  if (!value) return false;
  if (parsePositiveInt(value.id != null ? String(value.id) : undefined)) return true;
  return typeof value.identId === 'string' && value.identId.trim().length > 0;
};

/* ========================================================
   申出書詳細情報画面
======================================================== */
const ApplicationDocDetail = ({ data, setData }: ApplicationDocDetailProps) => {
  const navigate = useAppNavigate();
  const toast = useToast();
  const { applicationId } = useParams<{ applicationId?: string }>();
  const [searchParams] = useSearchParams();
  const numericApplicationId = parsePositiveInt(applicationId);
  const queryIdentId = (searchParams.get('identId') ?? '').trim();
  const dataApplicationId = parsePositiveInt(data?.id != null ? String(data.id) : undefined);
  const lookupIdentId = queryIdentId || (typeof data?.identId === 'string' ? data.identId.trim() : '');
  const shouldFetchInitialDoc = Boolean(numericApplicationId || queryIdentId || dataApplicationId || !hasApplicationDocData(data));

  const [doc, setDoc] = useState<AppDocDataLike | null>(
    numericApplicationId || queryIdentId ? null : hasApplicationDocData(data) ? (data ?? null) : null,
  );
  const [loading, setLoading] = useState(shouldFetchInitialDoc);
  const [showApproveConfirm, setShowApproveConfirm] = useState(false);
  const [showRejectConfirm, setShowRejectConfirm] = useState(false);
  const [showDenyConfirm, setShowDenyConfirm] = useState(false);
  const [approving, setApproving] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [denying, setDenying] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const attachmentWarningRef = useRef('');

  const load = async () => {
    const idToLoad = numericApplicationId ?? dataApplicationId;
    if (!idToLoad && !lookupIdentId && hasApplicationDocData(data)) {
      setDoc(data ?? null);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      if (!idToLoad && !lookupIdentId) {
        const response = await applicationDocsApi.list({ page: 0, size: 1 });
        const latestDoc = response.data.content[0] ?? null;
        setDoc(latestDoc);
        if (latestDoc) setData?.(latestDoc);
        return;
      }

      if (idToLoad) {
        const response = await applicationDocsApi.getById(idToLoad);
        setDoc(response.data);
        setData?.(response.data);
        return;
      }

      const response = await applicationDocsApi.getByIdentId(lookupIdentId);
      const latestDoc = response.data[0] ?? null;
      setDoc(latestDoc);
      if (latestDoc) setData?.(latestDoc);
    } catch (error: unknown) {
      if (isHandledAuthError(error)) return;
      navigate('recipient-search');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // 申請書類を取得する正当なデータ取得 effect。
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [numericApplicationId, queryIdentId, dataApplicationId, lookupIdentId]);

  const attachmentStorageAvailable = doc?.attachmentStorageAvailable;
  const attachmentStorageMessage = doc?.attachmentStorageMessage;
  const attachmentWarningDocId = doc?.id;
  const attachmentWarningIdentId = doc?.identId;

  useEffect(() => {
    if (attachmentStorageAvailable !== false) return;
    const message = String(attachmentStorageMessage || DEFAULT_ATTACHMENT_STORAGE_MESSAGE);
    const key = `${attachmentWarningDocId ?? attachmentWarningIdentId ?? 'current'}:${message}`;
    if (attachmentWarningRef.current === key) return;
    attachmentWarningRef.current = key;
    Modal.error({ content: message });
  }, [attachmentStorageAvailable, attachmentStorageMessage, attachmentWarningDocId, attachmentWarningIdentId]);

  const localDocMode = !numericApplicationId;
  const effectiveId = numericApplicationId ?? (doc?.id ? Number(doc.id) : null);
  const { role, hasPermission, hasAnyPermission } = usePermission();
  const canEditApp = hasAnyPermission('APPLICATION_EDIT_COMMENT', 'APPLICATION_EDIT_NON_COMMENT');
  const canEditAppNonComment = hasPermission('APPLICATION_EDIT_NON_COMMENT');
  const canReviewApp = hasPermission('APPLICATION_REVIEW');
  const canOperate = (Boolean(effectiveId) || (localDocMode && !!doc));
  // 承認 / 差戻 は案件ステータスが APP_UNDER_REVIEW（申出書確認中）のときのみ許可する。
  // ApplicationDocResponse.caseStatus は後端で i18n 済の日本語ラベルを返してくるため、
  // 日本語直比較ではなく caseStatusKeyByLabel で enum key に正規化してから比較する。
  const currentStatusKey = caseStatusKeyByLabel(doc?.caseStatus);
  const canReview = canOperate && canReviewApp && currentStatusKey === 'APP_UNDER_REVIEW';
  // 編集ボタンのラベル切替：ステータスがコメント以外編集不可の 5 完了系なら「編集（コメント）」表示。
  // welfareEnded は申出書詳細では参照不要（canEdit は status のみで判定するため false で固定）。
  const statusAllowsNonCommentEdit = caseStatusPermissions(currentStatusKey, false, role).canEdit;

  const updateLocalDoc = (next: AppDocDataLike) => {
    setDoc(next);
    setData?.(next);
  };

  const handleApprove = async () => {
    if (!canOperate || approving) return;
    setShowApproveConfirm(false);
    if (!effectiveId) {
      // 承認後の遷移先 = AWAITING_TRANSFER_LIST (給付額算出対象作成待)。
      // backend の APP_REVIEW_OK は APP_UNDER_REVIEW → AWAITING_TRANSFER_LIST。
      updateLocalDoc({ ...doc, caseStatus: CASE_STATUSES.AWAITING_TRANSFER_LIST });
      toast.success('承認しました');
      return;
    }
    setApproving(true);
    try {
      const response = await applicationDocsApi.approve(effectiveId);
      setDoc(response.data);
      setData?.(response.data);
      toast.success('承認しました');
    } catch (error) {
      if (isHandledAuthError(error)) return;
    } finally {
      setApproving(false);
    }
  };

  const handleReject = async () => {
    if (!canOperate || rejecting) return;
    setShowRejectConfirm(false);
    if (!effectiveId) {
      // ローカルモード（id 未確定）の楽観更新。値は CASE_STATUSES のラベル文字列で揃える。
      updateLocalDoc({ ...doc, caseStatus: CASE_STATUSES.APP_REJECTED });
      toast.success('差戻しました');
      return;
    }
    setRejecting(true);
    try {
      const response = await applicationDocsApi.reject(effectiveId, rejectReason);
      setDoc(response.data);
      setData?.(response.data);
      setRejectReason('');
      toast.success('差戻しました');
    } catch (error) {
      if (isHandledAuthError(error)) return;
    } finally {
      setRejecting(false);
    }
  };

  const handleDeny = async () => {
    if (!canOperate || denying) return;
    setShowDenyConfirm(false);
    if (!effectiveId) {
      updateLocalDoc({ ...doc, caseStatus: CASE_STATUSES.COMPLETED_DENIED });
      toast.success('不支給に確定しました');
      return;
    }
    setDenying(true);
    try {
      const response = await applicationDocsApi.deny(effectiveId);
      setDoc(response.data);
      setData?.(response.data);
      toast.success('不支給に確定しました');
    } catch (error) {
      if (isHandledAuthError(error)) return;
    } finally {
      setDenying(false);
    }
  };

  const appDocActionButtons = (variant: 'hero' | 'footer' = 'footer') => {
    const disabledClass = 'disabled:cursor-not-allowed disabled:opacity-50';
    return (
      <div className="flex flex-wrap gap-2 justify-end">
        {canEditApp && (
          <button
            type="button"
            className={`px-5 py-2 rounded-lg transition shadow-md cursor-pointer font-medium text-sm ${variant === 'hero' ? 'bg-white text-blue-600 hover:bg-blue-50' : 'bg-white border border-blue-200 text-blue-600 hover:bg-blue-50 shadow-sm'} ${disabledClass}`}
            disabled={!canOperate}
            onClick={() => {
              if (!canOperate) return;
              if (effectiveId) {
                navigate('application-document-edit', { applicationId: effectiveId });
              } else {
                navigate('application-document-edit');
              }
            }}
          >
            {canEditAppNonComment && statusAllowsNonCommentEdit ? '編集' : '編集（コメント）'}
          </button>
        )}
        {canReviewApp && (
          <button
            type="button"
            className={`px-5 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition shadow-md cursor-pointer font-medium text-sm ${disabledClass}`}
            disabled={!canReview || rejecting}
            onClick={() => setShowRejectConfirm(true)}
          >
            {rejecting ? '差戻中' : '差戻'}
          </button>
        )}
        {canReviewApp && (
          <button
            type="button"
            className={`px-5 py-2 bg-rose-700 text-white rounded-lg hover:bg-rose-800 transition shadow-md cursor-pointer font-medium text-sm ${disabledClass}`}
            disabled={!canReview || denying}
            onClick={() => setShowDenyConfirm(true)}
          >
            {denying ? '処理中' : '不支給'}
          </button>
        )}
        {canReviewApp && (
          <button
            type="button"
            className={`px-5 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition shadow-md cursor-pointer font-medium text-sm ${disabledClass}`}
            disabled={!canReview || approving}
            onClick={() => setShowApproveConfirm(true)}
          >
            {approving ? '承認中' : '承認'}
          </button>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-100">
      <SystemHeader breadcrumbs={[{ label: '受給者／申出書 検索', page: 'recipient-search' }, { label: '申出書詳細情報' }]} />
      <div className="mx-auto max-w-4xl px-6 py-8">
        <BackButton onClick={() => navigate('recipient-search')} className="mb-8" />
        {loading ? (
          <div className="flex min-h-[360px] items-center justify-center">
            <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : !doc ? (
          <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-lg">
            <p className="text-sm text-gray-600">申出書情報が見つかりません。</p>
            <button
              type="button"
              className="mt-4 rounded-lg border border-gray-300 bg-white px-5 py-2 text-sm text-gray-700 transition hover:bg-gray-50"
              onClick={() => navigate('recipient-search')}
            >
              一覧に戻る
            </button>
          </div>
        ) : (
          <>
            <div className="mb-4 flex flex-wrap items-center justify-end gap-3">
              {appDocActionButtons('footer')}
            </div>
            <div className="bg-gradient-to-r from-blue-600 to-blue-500 text-white rounded-xl shadow-lg p-6 mb-6">
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div className="min-w-0">
                  <p className="text-sm text-blue-100">申出書詳細情報</p>
                  <p className="text-2xl font-bold">{applicantNameOf(doc)}</p>
                  <div className="mt-2 flex flex-wrap gap-x-6 gap-y-1 text-sm text-blue-100">
                    <span>識別ID：<span className="font-mono font-semibold text-white">{doc.identId ?? '—'}</span></span>
                    <span>申請ID：<span className="font-mono font-semibold text-white">{formatApplicationDocDisplayId(doc)}</span></span>
                    <span>世帯番号：<span className="font-mono font-semibold text-white">{doc.householdNo ?? '—'}</span></span>
                  </div>
                </div>
                <span className="shrink-0">
                  <StatusBadge status={doc.caseStatus ?? doc.status ?? '—'} />
                </span>
              </div>
            </div>

            {doc.memo != null && String(doc.memo).trim() !== '' && (
              <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-6 mb-6">
                <h3 className="text-sm font-bold text-gray-800 mb-3">コメント</h3>
                <p className="text-sm text-gray-800 whitespace-pre-wrap min-h-[3rem]">
                  {doc.memo}
                </p>
              </div>
            )}

            <AppDocContentDisplay data={doc} hideMemo />

            {(() => {
              const logs = (doc as { rejectLogs?: { reason: string; rejectedByName: string; rejectedAt: string }[] | null }).rejectLogs;
              if (!logs || logs.length === 0) return null;
              return (
                <div className="bg-white rounded-2xl shadow-sm ring-1 ring-amber-200/70 overflow-hidden mt-5">
                  <div className="flex items-center gap-3 px-6 py-4 border-b border-amber-100 bg-gradient-to-r from-amber-50/70 via-white to-white">
                    <span className="inline-block w-1.5 h-5 rounded-full bg-amber-500 shrink-0" />
                    <h3 className="text-base font-bold text-amber-700">差戻履歴</h3>
                  </div>
                  <div className="p-5 md:p-6 space-y-3">
                    {logs.map((log, i) => (
                      <div key={i} className="border-l-4 border-amber-400 bg-amber-50/40 rounded-r-lg pl-4 pr-3 py-2.5">
                        <p className="text-sm text-gray-800 whitespace-pre-wrap">{log.reason}</p>
                        <p className="text-xs text-gray-500 mt-1">
                          {log.rejectedByName && <span className="mr-2 font-medium">{log.rejectedByName}</span>}
                          {log.rejectedAt ? new Date(log.rejectedAt).toLocaleString('ja-JP') : ''}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })()}

            <div className="mt-6 flex flex-wrap items-center justify-end gap-3">
              {appDocActionButtons('footer')}
            </div>
          </>
        )}

        {showApproveConfirm && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
            onClick={() => setShowApproveConfirm(false)}
            role="presentation"
          >
            <div
              className="bg-white rounded-xl p-6 shadow-2xl max-w-sm w-full mx-4"
              onClick={(e) => e.stopPropagation()}
              role="dialog"
              aria-modal="true"
            >
              <h3 className="text-lg font-bold text-gray-800 mb-2">承認確認</h3>
              <p className="text-sm text-gray-600 mb-6">この申出書を承認してもよろしいですか？</p>
              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  className="px-5 py-2 rounded-lg border border-gray-300 bg-white text-sm text-gray-700 hover:bg-gray-50 cursor-pointer"
                  onClick={() => setShowApproveConfirm(false)}
                  disabled={approving}
                >
                  キャンセル
                </button>
                <button
                  type="button"
                  className="px-5 py-2 rounded-lg bg-green-600 text-white text-sm font-medium hover:bg-green-700 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                  onClick={() => void handleApprove()}
                  disabled={approving}
                >
                  {approving ? '承認中...' : 'OK'}
                </button>
              </div>
            </div>
          </div>
        )}

        {showRejectConfirm && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
            onClick={() => { setShowRejectConfirm(false); setRejectReason(''); }}
            role="presentation"
          >
            <div
              className="bg-white rounded-xl p-6 shadow-2xl max-w-md w-full mx-4"
              onClick={(e) => e.stopPropagation()}
              role="dialog"
              aria-modal="true"
            >
              <h3 className="text-lg font-bold text-gray-800 mb-2">差戻確認</h3>
              <p className="text-sm text-gray-600 mb-4">この申出書を差戻してもよろしいですか？</p>
              <div className="mb-5">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  差戻理由 <span className="text-red-500">*</span>
                </label>
                <textarea
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-amber-400 resize-none"
                  rows={4}
                  placeholder="差戻理由を入力してください"
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  disabled={rejecting}
                />
              </div>
              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  className="px-5 py-2 rounded-lg border border-gray-300 bg-white text-sm text-gray-700 hover:bg-gray-50 cursor-pointer"
                  onClick={() => { setShowRejectConfirm(false); setRejectReason(''); }}
                  disabled={rejecting}
                >
                  キャンセル
                </button>
                <button
                  type="button"
                  className="px-5 py-2 rounded-lg bg-amber-600 text-white text-sm font-medium hover:bg-amber-700 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                  onClick={() => void handleReject()}
                  disabled={rejecting || rejectReason.trim() === ''}
                >
                  {rejecting ? '差戻中...' : 'OK'}
                </button>
              </div>
            </div>
          </div>
        )}

        {showDenyConfirm && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
            onClick={() => setShowDenyConfirm(false)}
            role="presentation"
          >
            <div
              className="bg-white rounded-xl p-6 shadow-2xl max-w-sm w-full mx-4"
              onClick={(e) => e.stopPropagation()}
              role="dialog"
              aria-modal="true"
            >
              <h3 className="text-lg font-bold text-gray-800 mb-2">不支給確定</h3>
              <p className="text-sm text-gray-600 mb-2">この申出書を不支給として最終確定します。</p>
              <p className="text-xs text-amber-700 mb-6">
                ※差戻と異なり、再提出はできません。後戻りしたい場合は「差戻」をご利用ください。
              </p>
              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  className="px-5 py-2 rounded-lg border border-gray-300 bg-white text-sm text-gray-700 hover:bg-gray-50 cursor-pointer"
                  onClick={() => setShowDenyConfirm(false)}
                  disabled={denying}
                >
                  キャンセル
                </button>
                <button
                  type="button"
                  className="px-5 py-2 rounded-lg bg-rose-700 text-white text-sm font-medium hover:bg-rose-800 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                  onClick={() => void handleDeny()}
                  disabled={denying}
                >
                  {denying ? '処理中...' : 'OK'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ApplicationDocDetail;
